import { Button } from "@veil/ui/components/button";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { ToggleGroup, ToggleGroupItem } from "@veil/ui/components/toggle-group";
import { formatRelative } from "@veil/ui/lib/format";
import Loader2 from "lucide-react/dist/esm/icons/loader-2";
import Play from "lucide-react/dist/esm/icons/play";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import { useState } from "react";
import { Link } from "wouter";
import { RenderBody, type RenderView, useRender } from "./render-tab";
import type { ClusterStats, PreviewResponse } from "./types";

/**
 * Render & Reload as a right-side drawer. Triggered from the cockpit
 * command bar so the diff preview and the apply step share one focused
 * context, instead of competing with the rest of the page for vertical
 * space. The render state machine lives in `useRender()` so the SSE
 * cleanup runs when the drawer unmounts.
 */
export function RenderDrawer({
	open,
	onOpenChange,
	stats,
	canApply,
	canEdit,
	clusterName,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	stats: ClusterStats | undefined;
	canApply: boolean;
	canEdit: boolean;
	clusterName: string;
}) {
	return (
		<ResponsiveDialog
			variant="sheet"
			side="right"
			open={open}
			onOpenChange={onOpenChange}
		>
			{/*
			 * Remount via `key={open}` so we get a clean render state machine
			 * (preview + SSE cleanup) every open. Otherwise a previous failed
			 * apply would persist in the drawer.
			 */}
			<ResponsiveDialogContent
				key={open ? "open" : "closed"}
				className="w-[min(48rem,100vw)]! max-w-none! gap-0 p-0 sm:max-w-none!"
			>
				<RenderDrawerInner
					open={open}
					onOpenChange={onOpenChange}
					stats={stats}
					canApply={canApply}
					canEdit={canEdit}
					clusterName={clusterName}
				/>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}

function RenderDrawerInner({
	open,
	onOpenChange,
	stats,
	canApply,
	canEdit,
	clusterName,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	stats: ClusterStats | undefined;
	canApply: boolean;
	canEdit: boolean;
	clusterName: string;
}) {
	const render = useRender({ active: open, canApply });
	const lastRender = stats?.lastRender?.finishedAt;
	const dirty = stats?.dirty === true;

	// Default the preview view to the most useful slice for the cluster's
	// state: Diff when dirty (the operator opened the drawer to push); Current
	// when clean (there's no diff to read, so jump straight to the live
	// config). Operators can flip via the segmented toggle either way.
	const [view, setView] = useState<RenderView>(dirty ? "diff" : "current");

	let headerCaption: string;
	if (render.phase === "preview") {
		if (dirty) {
			headerCaption = lastRender
				? `Live nginx.conf is older than the last edit. Last render ${formatRelative(lastRender)}.`
				: "Live nginx.conf is older than the last edit. No render yet.";
		} else {
			headerCaption = "Live nginx.conf is in sync with the database.";
		}
	} else if (render.phase === "applying") {
		headerCaption = "Validating, atomically swapping, and reloading openresty…";
	} else {
		headerCaption =
			render.applyStatus === "succeeded"
				? "Reload completed successfully."
				: "Reload failed — see log below.";
	}

	const showViewToggle =
		render.phase === "preview" && !!render.preview && !render.previewErr;

	return (
		<div className="flex h-full min-h-0 flex-col">
			<ResponsiveDialogHeader className="shrink-0 gap-1.5 border-b border-border/60 px-6 py-5">
				<div className="flex items-center gap-2 font-mono text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground">
					<span>{clusterName}</span>
					<span aria-hidden="true">·</span>
					<span>nginx.conf</span>
				</div>
				<ResponsiveDialogTitle className="text-lg leading-tight">
					Render &amp; Reload
				</ResponsiveDialogTitle>
				<ResponsiveDialogDescription className="text-pretty leading-relaxed">
					{headerCaption}
				</ResponsiveDialogDescription>
			</ResponsiveDialogHeader>

			<div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
				{showViewToggle && (
					<div className="mb-4 flex flex-wrap items-center justify-between gap-3">
						<ToggleGroup
							type="single"
							size="sm"
							variant="outline"
							value={view}
							onValueChange={(v) => {
								if (v) setView(v as RenderView);
							}}
							aria-label="Render view"
						>
							<ToggleGroupItem
								value="diff"
								disabled={render.isClean}
								title={
									render.isClean
										? "No changes to diff"
										: "Show added / removed lines"
								}
							>
								Diff
							</ToggleGroupItem>
							<ToggleGroupItem value="current" title="Live nginx.conf on disk">
								Current
							</ToggleGroupItem>
							<ToggleGroupItem
								value="next"
								title="What the cluster would render"
							>
								Next
							</ToggleGroupItem>
						</ToggleGroup>
						<ConfigStats preview={render.preview} view={view} />
					</div>
				)}
				<RenderBody render={render} view={view} />
			</div>

			<ResponsiveDialogFooter className="shrink-0 flex-row flex-wrap items-center justify-end gap-2 border-t border-border/60 bg-muted/40 px-6 py-4">
				{render.phase === "preview" && (
					<>
						<Button variant="outline" onClick={() => onOpenChange(false)}>
							Close
						</Button>
						<Button
							onClick={render.startApply}
							disabled={
								!canApply ||
								render.previewLoading ||
								!!render.previewErr ||
								!render.preview ||
								render.isClean
							}
							title={
								canApply
									? render.isClean
										? "Nothing to apply"
										: "Validate, swap, and reload openresty"
									: canEdit
										? "Admin role required to apply"
										: "Operator role required"
							}
						>
							<Play data-icon="inline-start" />
							{canApply ? "Apply" : "Apply (admin only)"}
						</Button>
					</>
				)}
				{render.phase === "applying" && (
					<Button disabled>
						<Loader2 data-icon="inline-start" className="animate-spin" />
						Running…
					</Button>
				)}
				{render.phase === "done" && (
					<>
						{render.operationId && (
							<Button variant="outline" asChild>
								<Link href={`/operations/${render.operationId}`}>
									Open operation
								</Link>
							</Button>
						)}
						{render.applyStatus === "failed" && (
							<Button onClick={render.retry}>
								<RefreshCcw data-icon="inline-start" />
								Retry
							</Button>
						)}
						{render.applyStatus === "succeeded" && (
							<>
								<Button variant="outline" onClick={render.retry}>
									Refresh preview
								</Button>
								<Button onClick={() => onOpenChange(false)}>Done</Button>
							</>
						)}
					</>
				)}
			</ResponsiveDialogFooter>
		</div>
	);
}

function ConfigStats({
	preview,
	view,
}: {
	preview: PreviewResponse | null;
	view: RenderView;
}) {
	if (!preview) return null;
	let label: string;
	let lines: number;
	if (view === "current") {
		label = "current";
		lines = countLines(preview.current);
	} else if (view === "next") {
		label = "next";
		lines = countLines(preview.next);
	} else {
		const { added, removed } = countDiff(preview.diff);
		return (
			<div className="font-mono text-[0.7rem] text-muted-foreground">
				<span className="text-emerald-700 dark:text-emerald-400">+{added}</span>
				<span aria-hidden="true"> · </span>
				<span className="text-destructive">−{removed}</span>
			</div>
		);
	}
	return (
		<div className="font-mono text-[0.7rem] text-muted-foreground">
			{lines} lines <span className="text-foreground/60">· {label}</span>
		</div>
	);
}

function countLines(s: string): number {
	if (!s) return 0;
	return s.split("\n").length;
}

function countDiff(s: string): { added: number; removed: number } {
	let added = 0;
	let removed = 0;
	for (const line of s.split("\n")) {
		if (line.startsWith("+")) added++;
		else if (line.startsWith("-")) removed++;
	}
	return { added, removed };
}
